import { answersToName, type PresetExportOption } from "./presetExport";

/** An article the case build asks the option database for by kind and name. */
export interface RequiredUserOption {
  readonly kind: string;
  readonly name: string;
  /**
   * Also looked up by the external configurator (`@emerge/lt-cases`) when it
   * builds a new case of any product (the factory settings of
   * `getSerializedDefaultVersion`).
   */
  readonly externalConfigurator: boolean;
}

/**
 * Every article the case build names in code and resolves in the options of
 * the database (`OptionDB.getByName`). A missing name makes the build take the
 * first article of the kind, or fail; the backend therefore refuses a change
 * that makes one of them stop resolving (`findBrokenRequiredOptions`).
 *
 * Kinds are the string values of `LTCaseUserOption` (`RackTailType` is
 * `RackRailType`, `TrayMaterial` is `TrayMaterialType`); a test in
 * `@project/manufacturer` ties the list to the enum and to the seed. An article
 * answers to its name or to an alias (`STW1378` answers to `M1378`). Lookups
 * inside build steps are not found automatically: add a new one here.
 */
export const REQUIRED_USER_OPTIONS: readonly RequiredUserOption[] = [
  { kind: "AngleSectionType", name: "M1377", externalConfigurator: true },
  { kind: "AngleSectionType", name: "M1378", externalConfigurator: true },
  { kind: "CornerBraceType", name: "AH4043A", externalConfigurator: true },
  { kind: "CornerType", name: "AH41201LT", externalConfigurator: true },
  { kind: "DishType", name: "AH88001D", externalConfigurator: false },
  { kind: "FootType", name: "AH4904", externalConfigurator: false },
  { kind: "HandleType", name: "AH3415", externalConfigurator: false },
  { kind: "HingeType", name: "AH2524", externalConfigurator: false },
  { kind: "LockType", name: "AH17291S", externalConfigurator: true },
  { kind: "PaddingType", name: "nopad", externalConfigurator: true },
  { kind: "PaddingType", name: "SST1042", externalConfigurator: false },
  { kind: "PaddingType", name: "SST1043", externalConfigurator: true },
  { kind: "PaddingType", name: "SST1056", externalConfigurator: false },
  { kind: "PanelColor", name: "Phenol", externalConfigurator: true },
  {
    kind: "PanelColor",
    name: "schokobraun (RAL8017)",
    externalConfigurator: false,
  },
  { kind: "ProfileType", name: "M6102", externalConfigurator: true },
  { kind: "RackTailType", name: "STW6162", externalConfigurator: true },
  { kind: "RollerType", name: "AH 37600S rechts", externalConfigurator: true },
  { kind: "RollerType", name: "Keine", externalConfigurator: true },
  { kind: "RunnerType", name: "1004", externalConfigurator: false },
  { kind: "TrayMaterial", name: "PLW1415", externalConfigurator: true },
  { kind: "TrayMaterial", name: "PLW1418", externalConfigurator: true },
  { kind: "TrolleyHandleType", name: "AH34703", externalConfigurator: false },
];

/** The fields of an option the rule reads. */
export type RequiredOptionCandidate = Pick<
  PresetExportOption,
  "id" | "kind" | "name" | "aliases" | "deleted"
>;

/** The fields of a change that can alter which option answers to a name. */
export const REQUIRED_OPTION_FIELDS: ReadonlySet<string> = new Set([
  "name",
  "kind",
  "aliases",
  "deleted",
]);

const answers = (
  option: RequiredOptionCandidate,
  required: RequiredUserOption,
) =>
  !option.deleted &&
  option.kind === required.kind &&
  answersToName(
    {
      name: option.name,
      aliases: Array.isArray(option.aliases) ? option.aliases : undefined,
    },
    required.name,
  );

/**
 * The option a required article resolves to: the first one in list order that
 * is not deleted and answers to the name (the option database takes the first
 * too). Pass the list unsorted.
 */
export function resolveRequiredOption<T extends RequiredOptionCandidate>(
  options: readonly T[],
  required: RequiredUserOption,
): T | undefined {
  return options.find((option) => answers(option, required));
}

/**
 * The required articles that resolved before and no longer do after a change.
 * Only a transition counts: an article the catalogue never had is not
 * reported, so an empty or partly filled database stays editable.
 */
export function findBrokenRequiredOptions(
  before: readonly RequiredOptionCandidate[],
  after: readonly RequiredOptionCandidate[],
  required: readonly RequiredUserOption[] = REQUIRED_USER_OPTIONS,
): RequiredUserOption[] {
  return required.filter(
    (article) =>
      resolveRequiredOption(before, article) !== undefined &&
      resolveRequiredOption(after, article) === undefined,
  );
}

/** The ids of the options that currently hold a required article in place. */
export function requiredUserOptionIds(
  options: readonly RequiredOptionCandidate[],
): Set<string> {
  const ids = new Set<string>();
  for (const article of REQUIRED_USER_OPTIONS) {
    const id = resolveRequiredOption(options, article)?.id;
    if (id) ids.add(id);
  }
  return ids;
}

/**
 * Names for the rule sentence: a few articles as one text, because a sentence
 * parameter loses its leading space on the wire, and the rest as a count.
 */
export function describeBrokenRequiredOptions(
  broken: readonly RequiredUserOption[],
  limit = 5,
): { articles: string; more: number } {
  const names = [...new Set(broken.map((article) => article.name))];
  return {
    articles: names.slice(0, limit).join(", "),
    more: Math.max(0, names.length - limit),
  };
}
